import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

interface ArticleLayoutProps {
  title: string;
  description: string;
  tags: string[];
  readTime: string;
  tocItems: { id: string; title: string }[];
  heroImage?: string;
  children: React.ReactNode;
}

export default function ArticleLayout({ title, description, tags, readTime, tocItems, heroImage, children }: ArticleLayoutProps) {
  const location = useLocation();
  const siteOrigin = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.fit';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    author: { '@type': 'Organization', name: 'Basetao Sheet' },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Sheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
      },
    },
    datePublished: '2026-08-18',
    dateModified: '2026-08-18',
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteOrigin}${location.pathname}` },
    inLanguage: 'en',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-400">Article</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{title.length > 40 ? title.slice(0, 40) + '...' : title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">{tag}</span>
              ))}
              <span className="text-xs text-foreground-500">{readTime}</span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-5">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {description}
            </p>
          </header>

          {/* Hero Image */}
          {heroImage && (
            <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img src={heroImage} alt={title} className="w-full h-full object-cover object-top" />
            </div>
          )}

          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6 mb-10" aria-label="Table of Contents">
            <h2 className="text-sm font-semibold text-foreground-900 mb-4">Table of Contents</h2>
            <ol className="space-y-2 text-sm">
              {tocItems.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-foreground-600 hover:text-primary-600 transition-colors flex items-start gap-2">
                    <span className="text-foreground-400 font-medium min-w-[1.25rem]">{i + 1}.</span>
                    {item.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Article Body */}
          <div className="prose-custom space-y-12 md:space-y-16">
            {children}
          </div>

          {/* Category Links */}
          <div className="mt-14 pt-10 border-t border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-900 mb-5">Explore Product Categories on Basetao</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors"
                >
                  <img src={cat.image} alt={cat.name} className="w-6 h-6 rounded object-cover flex-shrink-0" />
                  <span className="whitespace-nowrap">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Back to Home */}
          <div className="mt-10 pt-6 border-t border-background-200/70 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Back to Basetao Sheet Homepage
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}

export function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id}>
      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-5">{title}</h2>
      <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function QuickAnswerBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-accent-50 border border-accent-200/70 rounded-xl p-5 md:p-6 mb-10">
      <p className="text-xs font-semibold text-accent-700 uppercase tracking-wider mb-2">Quick Answer</p>
      <div className="text-sm text-foreground-700 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

export function CheckList({ items }: { items: { bold: string; text: string }[] }) {
  return (
    <ul className="space-y-2 mt-3">
      {items.map((item) => (
        <li key={item.bold} className="flex items-start gap-2 text-sm text-foreground-600">
          <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
          <span><strong>{item.bold}</strong> {item.text}</span>
        </li>
      ))}
    </ul>
  );
}